<template>
  <div>
    <div v-if="data?.type == 'code'">
      <pre>{{ data }}</pre>
    </div>
    <el-card :body-style="{ padding: '0px' }" class="mt-3">
      <RatioImage
        v-if="data?.banner_media?.url"
        ratio="16:9"
        :src="imgProxy(data?.banner_media?.url)"
        :tip="dt(data?.title_translations)"
      />

      <div class="p-2">
        <div class="text-primary h5 my-2">{{ dt(data?.title_translations) }}</div>

        <div v-if="hideCondition" class="alert alert-primary">
          <div v-for="condition in data?.conditions">
            <span v-if="data?.condition_min_type == 'amount'">
                {{ t("promotions.fill") }}
                {{ condition?.min_amount }}
            </span>
            <span v-else-if="data?.condition_min_type == 'qty'">
                {{ t("promotions.fill") }} {{ condition?.min_qty }} {{ t("promotions.fillUnit") }}
            </span>
            <span v-if="data?.discount_type == 'amount'">
                {{ t("promotions.discounts") }}
                {{ condition?.discount_amount }}
            </span>
            <span v-else-if="data?.discount_type == 'percentage'">
                {{ t("promotions.discounts") }}
                {{ condition?.discount_percentage }} %
            </span>
            <span v-else-if="data?.discount_type == 'gift'">
                {{ t("promotions.freeGift") }}
            </span>
            <span v-else-if="data?.discount_type == 'pricing'">
                固定
                {{ condition?.discounted_price }}
            </span>
          </div>
        </div>

        <div class="bottom clearfix">
          <div class="w-100 d-flex flex-wrap my-2">
            <div class="w-auto text-muted mr-2">活動狀態:</div>
            <div class="text-info w-75">
              {{ promotionDuration }}
            </div>
          </div>

          <div v-if="term_translations" class="w-100 d-flex flex-wrap my-2">
            <div class="w-auto text-muted mr-2">活動細則:</div>
            <div class="text-info w-75">
                {{ term_translations }}
            </div>
          </div>

          <div class="w-100 d-flex flex-wrap my-2">
            <div class="w-auto text-muted mr-2">付款方式:</div>
            <div class="text-info w-75">
                {{ whitelistedPaymentNames.join(" | ") }}

            </div>
          </div>
          <div class="w-100 d-flex flex-wrap my-2">
            <div class="w-auto text-muted mr-2">配送方式:</div>
            <div class="text-info w-75">
              {{ whitelistedLogisticNames.join(" | ") }}
            </div>
          </div>

          <div class="w-100 d-flex flex-wrap my-2">
            <div class="w-auto text-muted mr-2">免運規則:</div>
            <div class="text-info w-75">
              <span v-if="data?.min_amount_free_shipping">
                滿 {{ currencyFormat(data?.min_amount_free_shipping) }} 免運
                <span v-if="data?.is_excluded_outer_island_free_shipping">
                  (外島不適用)
                </span>
              </span>
              <span v-else>無</span>
            </div>
          </div>

          <div v-if="!!data?.supplier_name" class="w-100 d-flex flex-wrap my-2">
            <div class="w-auto text-muted mr-2">出貨廠商:</div>
            <div class="text-info w-75">
              {{ data?.supplier_name }}
            </div>
          </div>
        </div>

        <el-divider v-if="!!data?.whitelisted_store_products && !!data?.whitelisted_store_products.length" />

        <div class="mx-auto d-flex overflow-auto">
          <PromotionProductItem
            v-for="item in data?.whitelisted_store_products"
            :key="item.id"
            :item="item"
            :display-coin-reward="!!data?.display_coin_reward"
            :is_login="is_login"
            :is-distribute="isDistribute"
            :store-data="storeData"
            @add="handleAddToCart"
            @quick-add="handleQuickAddToCart"
            @show="handleShowItem"
            @show-coin="showCoinLevel"
            @view="handleQuickView"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from "vue-i18n"
import { dataTranslations as dt, dateFormat, imgProxy, currencyFormat } from "../../utils"
import RatioImage from '../RatioImage.vue'
import PromotionProductItem from "./PromotionProductItem2.vue"

const props = defineProps({
  data: { type: Object, required: true },
  storeData: { type: Object, required: true },
  is_login: { type: Boolean, default: false },
  isDistribute: { type: Boolean, default: false },
})
const emit = defineEmits(['add', 'quick-add', 'show', 'show-coin', 'view'])

const { t, n, locale } = useI18n()
const payments = computed(() => props.storeData?.payments || [])
const logistics = computed(() => props.storeData?.logistics || [])
const hideCondition = computed(() => (props.data?.condition_min_type == "non" || props.data?.discount_type == "non") ? false : true)

const whitelistedPaymentNames = computed(() => {
    let whitelisted_payment_names = []
    if (!props.data?.id) {
      return whitelisted_payment_names
    }
    payments.value.forEach((payment) => {
        // if (props.data?.whitelisted_payment_types.includes(payment.type)) {
            whitelisted_payment_names.push(dt(payment.name_translations))
        // }
    })
    return whitelisted_payment_names
})
const whitelistedLogisticNames = computed(() => {
    let whitelisted_logistic_names = []
    if (!props.data?.id) {
      return whitelisted_logistic_names
    }
    logistics.value.forEach((logistic) => {
        // if (props.data?.whitelisted_delivery_types.includes(logistic.delivery_type)) {
            whitelisted_logistic_names.push(dt(logistic.name_translations))
        // }
    })
    return whitelisted_logistic_names
})

const term_translations = computed(() => {
  if (!props.data?.id) {
      return null
  }
  if (Object.keys(props.data.term_translations).length) {
    let term = dt(props.data.term_translations)
    return term
  }
  return null
})

const promotionDuration = computed(() => {
    if (!props.data?.id) {
        return "";
    }
    const { start_at, ended_at } = props.data
    if (!start_at) {
      return "";
    }
    const timeNow = dayjs()
    if (timeNow < dayjs(start_at)) {
      return `活動將在 ${dayjs(start_at).format("YYYY-MM-DD hh:mm a")} 開始}`
    }
    if (ended_at) {
      if (timeNow > dayjs(ended_at)) {
        return t("活動已結束");
      } else {
        return `活動進行中 (${dayjs(ended_at).format("YYYY-MM-DD hh:mm a")} 截止)`;
      }
    } else {
      return "活動進行中 (無截止日期)";
    }
})

const handleAddToCart = (item) => {
  // console.debug("handleAddToCart", item)
  emit("add", item)
}
const handleQuickAddToCart = (item) => {
  // console.debug("handleQuickAddToCart", item)
  emit("quick-add", item)
}
const handleShowItem = (item) => {
  // console.debug("handleShowItem", item)
  emit("show", item)
}
const showCoinLevel = (item) => {
  emit("show-coin", item)
  // console.debug("showCoinLevel", item)
}
const handleQuickView = (item) => {
  // console.debug("handleQuickView", item)
  emit("view", item)
}

</script>
